import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/auth/LoginPage";
import OTPPage from "@/pages/auth/OTPPage";
import LandingPage from "@/pages/LandingPage";
import HomePage from "@/pages/HomePage";
import SplitNewPage from "@/pages/SplitNewPage";
import ExpenseDetailPage from "@/pages/ExpenseDetailPage";
import ExpenseRequestPage from "@/pages/ExpenseRequestPage";
import ExpenseConfirmPage from "@/pages/ExpenseConfirmPage";
import ProfilePage from "@/pages/ProfilePage";
import GroupsPage from "@/pages/GroupsPage";
import CreateGroupPage from "@/pages/CreateGroupPage";
import GroupDetailPage from "@/pages/GroupDetailPage";
import GroupExpensePage from "@/pages/GroupExpensePage";
import PaymentPage from "@/pages/PaymentPage";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }
  
  return children;
};

// Public Route component (redirect to home if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

// Root redirect
const RootRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  // If authenticated, go to home. Otherwise show landing page.
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  
  return <LandingPage />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />
      
      {/* Auth routes (public) */}
      <Route path="/auth/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/auth/otp" element={
        <PublicRoute>
          <OTPPage />
        </PublicRoute>
      } />
      
      {/* Public payment page (no auth required) */}
      <Route path="/pay/:expenseId" element={<PaymentPage />} />
      
      {/* Protected routes */}
      <Route path="/home" element={
        <ProtectedRoute>
          <HomePage />
        </ProtectedRoute>
      } />
      <Route path="/split/new" element={
        <ProtectedRoute>
          <SplitNewPage />
        </ProtectedRoute>
      } />
      <Route path="/expenses/:id" element={
        <ProtectedRoute>
          <ExpenseDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/expenses/:id/request" element={
        <ProtectedRoute>
          <ExpenseRequestPage />
        </ProtectedRoute>
      } />
      <Route path="/expenses/:id/confirm" element={
        <ProtectedRoute>
          <ExpenseConfirmPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/groups" element={
        <ProtectedRoute>
          <GroupsPage />
        </ProtectedRoute>
      } />
      <Route path="/groups/new" element={
        <ProtectedRoute>
          <CreateGroupPage />
        </ProtectedRoute>
      } />
      <Route path="/groups/:groupId" element={
        <ProtectedRoute>
          <GroupDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/groups/:groupId/expense" element={
        <ProtectedRoute>
          <GroupExpensePage />
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App font-body">
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-center" />
      </div>
    </AuthProvider>
  );
}

export default App;
